<template>
  <b-modal
      :id="id"
      :title="title"
      dialog-class="bulk-create-dialog"
      :ok-title="okTitle"
      ok-variant="success"
      :ok-disabled="!workoutsFile || creating"
      @ok="onCreate"
      @show="onShow"
      @shown="toggleDropListeners(true)"
      @hidden="toggleDropListeners(false)"
  >
    <div class="bulk-drop-zone mb-3" :class="{dragging: dragDepth > 0}">
      <font-awesome-icon icon="upload" class="mr-1"/>
      Drop the .yaml workouts file and the .json manifest here, or choose them below
    </div>
    <b-form-group label="Workouts (YAML)" label-size="sm">
      <b-form-file ref="workoutsInput" v-model="workoutsFile" accept=".yaml,.yml" placeholder="Choose a YAML file"/>
    </b-form-group>
    <b-form-group label="Manifest: labels and program weeks (JSON, optional)" label-size="sm"
                  :description="program ? 'Weeks come from the manifest. Workouts without weeks are added to the program without a week.' : null">
      <b-form-file ref="manifestInput" v-model="manifestFile" accept=".json" placeholder="Choose a JSON file"/>
    </b-form-group>
    <!-- Without a given program, the user can pick one -->
    <template v-if="!program">
      <b-form-group v-if="programOptions.length > 1" label="Add all the workouts to program (optional)" label-size="sm"
                    description="Weeks come from the manifest. Workouts without weeks are added to the program without a week.">
        <b-form-select v-model="selectedProgramUuid" :options="programOptions"/>
      </b-form-group>
      <b-alert :show="programsError" variant="warning" class="small">
        Couldn't load the programs, so the workouts can't be added to a program right now.
      </b-alert>
    </template>
    <b-alert :show="errors.length > 0" variant="danger" class="small mb-0">
      <div class="mb-2">
        <b>Nothing was created.</b>
        <span v-if="failedCount > 0">
          {{ failedCount }} of {{ total }} workouts have problems.
        </span>
        Fix the files and try again.
      </div>
      <div class="bulk-errors">
        <div v-for="(error, i) in errors" :key="i" class="mb-2">
          <div v-if="error.workout" class="font-weight-bold">
            {{ error.workout }}
            <span v-if="error.line" class="font-weight-normal text-muted">(line {{ error.line }})</span>
          </div>
          <ul class="mb-0 pl-3">
            <li v-for="(message, j) in error.messages" :key="j">{{ message }}</li>
          </ul>
        </div>
      </div>
    </b-alert>
  </b-modal>
</template>

<script>

/**
 * Creates workouts from a YAML file (one workout per document) and an optional JSON manifest (labels and program weeks).
 * Emits "created" with the new workouts once the server accepted them all.
 */
export default {
  name: 'BulkCreateWorkoutsModal',
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: 'Bulk Create Workouts',
    },
    okTitle: {
      type: String,
      default: 'Create',
    },
    // {uuid, name}: add all the workouts to this program, instead of letting the user pick one
    program: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      workoutsFile: null,
      manifestFile: null,
      errors: [],
      total: 0,
      failedCount: 0,
      creating: false,
      dragDepth: 0,
      programs: [],
      selectedProgramUuid: null,
      programsError: false,
    }
  },
  computed: {
    programOptions() {
      return [
        {value: null, text: 'None'},
        ...this.programs.map(program => ({value: program.uuid, text: program.name})),
      ];
    },
    targetProgram() {
      return this.program || this.programs.find(item => item.uuid === this.selectedProgramUuid) || null;
    },
  },
  beforeDestroy() {
    this.toggleDropListeners(false);
  },
  methods: {
    onShow() {
      this.workoutsFile = null;
      this.manifestFile = null;
      this.selectedProgramUuid = null;
      this.programsError = false;
      this.setErrors([]);
      if (this.program) {
        return;
      }
      this.$http.get('/api/programs')
          .then(response => {
            this.programs = response.data;
          })
          .catch(e => {
            this.programs = [];
            // Users who can't manage programs don't get the option, anything else is a problem worth showing
            this.programsError = !(e.response && e.response.status === 403);
          })
    },
    toggleDropListeners(enable) {
      // Listen on the window in the capture phase, so a file dropped anywhere (even on the other input) is routed by its extension
      const method = enable ? window.addEventListener : window.removeEventListener;
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(type => method(type, this.onDragEvent, true));
      this.dragDepth = 0;
    },
    onDragEvent(event) {
      if (!event.dataTransfer || !Array.from(event.dataTransfer.types).includes('Files')) {
        return;
      }
      // Stop the browser from opening the file, and the inputs from handling the drop themselves
      event.preventDefault();
      event.stopPropagation();

      if (event.type === 'dragenter') {
        this.dragDepth++;
      } else if (event.type === 'dragleave') {
        this.dragDepth = Math.max(0, this.dragDepth - 1);
      } else if (event.type === 'dragover') {
        event.dataTransfer.dropEffect = 'copy';
      } else if (event.type === 'drop') {
        this.dragDepth = 0;
        this.onFilesDropped(Array.from(event.dataTransfer.files));
      }
    },
    onFilesDropped(files) {
      const extension = file => file.name.split('.').pop().toLowerCase();
      const yamlFiles = files.filter(file => ['yaml', 'yml'].includes(extension(file)));
      const jsonFiles = files.filter(file => extension(file) === 'json');
      const messages = files
          .filter(file => !yamlFiles.includes(file) && !jsonFiles.includes(file))
          .map(file => `${file.name}: only .yaml, .yml and .json files are supported`);

      // setFiles() updates the input's displayed file name as well as its v-model
      if (yamlFiles.length > 1) {
        messages.push('Drop a single .yaml workouts file');
      } else if (yamlFiles.length === 1) {
        this.$refs.workoutsInput.setFiles(yamlFiles);
      }
      if (jsonFiles.length > 1) {
        messages.push('Drop a single .json manifest');
      } else if (jsonFiles.length === 1) {
        this.$refs.manifestInput.setFiles(jsonFiles);
      }
      this.setErrors(messages.map(message => ({messages: [message]})));
    },
    setErrors(errors, total = 0, failedCount = 0) {
      this.errors = errors;
      this.total = total;
      this.failedCount = failedCount;
    },
    async onCreate(event) {
      // Keep the modal open until the server accepts the workouts
      event.preventDefault();
      this.setErrors([]);

      let manifest = null;
      if (this.manifestFile) {
        try {
          manifest = JSON.parse(await this.manifestFile.text());
        } catch (e) {
          this.setErrors([{messages: [`Invalid manifest JSON: ${e.message}`]}]);
          return;
        }
      }

      const program = this.targetProgram;
      this.creating = true;
      try {
        const response = await this.$http.post(`/api/workouts/bulk-create`, {
          workouts_yaml: await this.workoutsFile.text(),
          manifest: manifest,
          program: program ? {uuid: program.uuid} : null,
        });
        this.showCreatedToast(response.data.length, program);
        this.$bvModal.hide(this.id);
        this.$emit('created', response.data);
      } catch (e) {
        const data = e.response ? e.response.data : null;
        if (data && data.errors) {
          this.setErrors(data.errors, data.total, data.failed);
        } else {
          this.setErrors([{messages: ['Bulk create failed']}]);
        }
      } finally {
        this.creating = false;
      }
    },
    showCreatedToast(count, program) {
      const created = `${count} workouts created`;
      // With a program, the message links to its page (so the toast stays longer, and can be closed)
      const h = this.$createElement;
      const message = program
          ? h('span', [
            `${created}, added to `,
            h('b-link', {props: {to: {name: 'program', params: {uuid: program.uuid}}}}, program.name),
          ])
          : created;
      this.$bvToast.toast(message, {
        title: this.title,
        toaster: 'b-toaster-bottom-center',
        variant: 'success',
        solid: true,
        autoHideDelay: program ? 6000 : 2000,
        noCloseButton: !program,
        appendToast: true,
      });
    },
  }
}
</script>

<style>
/* Modals render outside this component, so this block can't be scoped */
@media (min-width: 576px) {
  .modal-dialog.bulk-create-dialog {
    max-width: 1000px; /* twice Bootstrap's default 500px */
    margin-top: calc(1.75rem + 60px);
  }
}

.bulk-create-dialog .bulk-drop-zone {
  border: 2px dashed #ced4da;
  border-radius: 0.25rem;
  padding: 1.5rem;
  text-align: center;
  color: #6c757d;
  transition: background-color 0.15s, border-color 0.15s;
}

.bulk-create-dialog .bulk-drop-zone.dragging {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.08);
  color: #28a745;
}

.bulk-create-dialog .bulk-errors {
  max-height: 50vh;
  overflow-y: auto;
}
</style>
